import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/* ── Types ──────────────────────────────────────────────────────────────── */

interface PaletteResult {
  label: string;
  icon: string;
  type: 'navigate' | 'pipeline' | 'heal' | 'brand' | 'action' | 'view';
  slug?: string;
  view?: string;
  brand?: string;
  action?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  pieces: { slug: string; lifecycleState?: string }[];
  volumes: { slug: string; title?: string }[];
  brand: string;
  brands: string[];
  onSelectNode: (slug: string) => void;
  onAction: (action: string, slug: string) => void;
  onSwitchBrand: (brand: string) => void;
  onNavigateView: (view: string) => void;
  attentionManaged?: boolean;
}

// #285 B7: ⌘K can jump to any of the app's views (was pieces/actions only).
const VIEW_DESTINATIONS: { view: string; label: string }[] = [
  { view: 'library', label: 'Library' }, { view: 'ebooks', label: 'Ebooks' },
  { view: 'newsletter', label: 'Newsletter' }, { view: 'social-studio', label: 'Social Studio' },
  { view: 'attention', label: 'Attention' },
  { view: 'quality', label: 'Quality' }, { view: 'brand-performance', label: 'Brand Performance' },
  { view: 'channels', label: 'Channels' }, { view: 'linkedin', label: 'LinkedIn' },
  { view: 'outreach', label: 'Outreach' }, { view: 'comments-approval', label: 'Comments' },
  { view: 'settings', label: 'Settings' }, { view: 'activity', label: 'Activity' },
];

export function availableViewDestinations(attentionManaged: boolean): typeof VIEW_DESTINATIONS {
  const legacyAttentionDestinations = new Set(['linkedin', 'comments-approval']);
  return attentionManaged
    ? VIEW_DESTINATIONS.filter((destination) => !legacyAttentionDestinations.has(destination.view))
    : VIEW_DESTINATIONS;
}

/* ── Component ──────────────────────────────────────────────────────────── */

export default function CommandPalette({
  open,
  onClose,
  pieces,
  volumes,
  brand,
  brands,
  onSelectNode,
  onAction,
  onSwitchBrand,
  onNavigateView,
  attentionManaged = false,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Build results from query. On an empty query we surface the view
  // destinations (jump-to-anywhere, #285 B7); typing filters views + pieces +
  // actions.
  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    const destinations = availableViewDestinations(attentionManaged);
    // Empty query: offer the view destinations so ⌘K is a jump-to-anywhere.
    if (!q) return destinations.map((d): PaletteResult => ({ label: `Go to ${d.label}`, icon: '⌘', type: 'view', view: d.view }));

    const out: PaletteResult[] = [];
    const actionOnly = q.startsWith('>');
    const searchTerm = actionOnly ? q.slice(1).trim() : q;
    // `>` alone (no filter term yet) also stays empty so we don't flood the
    // list with every pipeline + brand action until the user actually types.
    if (!searchTerm) return [];

    // Humanize slug
    const hTitle = (s: string) => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    // Navigation results
    if (!actionOnly) {
      for (const d of destinations) {
        const label = `Go to ${d.label}`;
        if (label.toLowerCase().includes(searchTerm) || d.view.includes(searchTerm)) {
          out.push({ label, icon: '⌘', type: 'view', view: d.view });
        }
      }
      for (const p of pieces) {
        const title = hTitle(p.slug);
        const label = `Go to ${title}`;
        if (label.toLowerCase().includes(searchTerm) || p.slug.toLowerCase().includes(searchTerm)) {
          out.push({ label, icon: '\u2192', type: 'navigate', slug: p.slug });
        }
      }
      for (const v of volumes) {
        const title = v.title || hTitle(v.slug);
        const label = `Go to ${title}`;
        if (label.toLowerCase().includes(searchTerm) || v.slug.toLowerCase().includes(searchTerm)) {
          out.push({ label, icon: '\u2192', type: 'navigate', slug: v.slug });
        }
      }
    }

    // Pipeline actions
    for (const p of pieces) {
      const runLabel = `Run Pipeline ${hTitle(p.slug)}`;
      if (runLabel.toLowerCase().includes(searchTerm) || p.slug.toLowerCase().includes(searchTerm)) {
        out.push({ label: runLabel, icon: '\u25B6', type: 'pipeline', slug: p.slug });
      }
      const healLabel = `Improve quality: ${hTitle(p.slug)}`;
      if (healLabel.toLowerCase().includes(searchTerm) || p.slug.toLowerCase().includes(searchTerm)) {
        out.push({ label: healLabel, icon: '\u2726', type: 'heal', slug: p.slug });
      }
    }

    // Brand switching
    for (const b of brands) {
      if (b === brand) continue;
      const label = `Switch brand: ${b}`;
      if (label.toLowerCase().includes(searchTerm) || b.toLowerCase().includes(searchTerm)) {
        out.push({ label, icon: '\u25C6', type: 'brand', brand: b });
      }
    }

    return out;
  }, [query, pieces, volumes, brand, brands, attentionManaged]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after render
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Clamp selection index when results change
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(0, results.length - 1)));
  }, [results.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const selected = resultsRef.current.querySelector('[data-selected="true"]');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const executeResult = useCallback(
    (index: number) => {
      const r = results[index];
      if (!r) return;
      onClose();

      switch (r.type) {
        case 'navigate':
          if (r.slug) onSelectNode(r.slug);
          break;
        case 'pipeline':
          if (r.slug) onAction('pipeline', r.slug);
          break;
        case 'heal':
          if (r.slug) onAction('heal', r.slug);
          break;
        case 'brand':
          if (r.brand) onSwitchBrand(r.brand);
          break;
        case 'view':
          if (r.view) onNavigateView(r.view);
          break;
      }
    },
    [results, onClose, onSelectNode, onAction, onSwitchBrand, onNavigateView],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        executeResult(selectedIndex);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [results.length, selectedIndex, executeResult, onClose],
  );

  if (!open) return null;

  const MAX_DISPLAY = 12;
  const displayed = results.slice(0, MAX_DISPLAY);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '20vh',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        style={{
          width: 520,
          maxHeight: '60vh',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
        }}
      >
        {/* Search Input */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pieces, run commands..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              outline: 'none',
              color: 'var(--text-primary)',
              fontSize: 15,
              fontFamily: 'inherit',
              padding: 0,
            }}
          />
        </div>

        {/* Results */}
        <div
          ref={resultsRef}
          style={{
            overflow: 'auto',
            padding: '4px 0',
          }}
        >
          {displayed.length === 0 && query.trim().length > 0 && (
            <div
              style={{
                padding: '20px 16px',
                color: 'var(--text-tertiary)',
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              No results
            </div>
          )}

          {displayed.map((r, i) => (
            <div
              key={`${r.type}-${r.slug ?? r.view ?? r.brand ?? r.action ?? ''}-${i}`}
              data-selected={i === selectedIndex}
              onClick={() => executeResult(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
                cursor: 'pointer',
                background: i === selectedIndex ? 'var(--accent-muted)' : 'transparent',
                transition: 'background 0.1s',
              }}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              <span
                style={{
                  width: 20,
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--text-tertiary)',
                  flexShrink: 0,
                }}
              >
                {r.icon}
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: i === selectedIndex ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                {r.label}
              </span>
            </div>
          ))}

          {results.length > MAX_DISPLAY && (
            <div
              style={{
                padding: '8px 16px',
                fontSize: 11,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              {results.length - MAX_DISPLAY} more results...
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            gap: 16,
            fontSize: 11,
            color: 'var(--text-tertiary)',
          }}
        >
          <span>
            <kbd style={kbdStyle}>&uarr;</kbd>
            <kbd style={kbdStyle}>&darr;</kbd> navigate
          </span>
          <span>
            <kbd style={kbdStyle}>Enter</kbd> execute
          </span>
          <span>
            <kbd style={kbdStyle}>Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 5px',
  borderRadius: 0,
  border: '1px solid var(--border-default)',
  background: 'var(--bg-raised)',
  fontSize: 10,
  fontFamily: 'inherit',
  marginRight: 3,
};
